import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { CircleIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils/cn";

function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn("grid gap-3", className)}
      {...props}
    />
  );
}

function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        "border-neutral-300 dark:border-border hover:border-neutral-400 dark:hover:border-neutral-500 data-[state=checked]:border-primary data-[state=checked]:hover:border-primary text-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon className="fill-primary absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

type RadioGroupCardProps = React.ComponentProps<typeof RadioGroupPrimitive.Item> & {
  label: React.ReactNode;
  description?: React.ReactNode;
};

function RadioGroupCard({
  className,
  id,
  label,
  description,
  disabled,
  ...props
}: RadioGroupCardProps) {
  return (
    <label
      htmlFor={id}
      data-slot="radio-group-card"
      className={cn(
        "flex items-start gap-3 rounded-lg border p-4 transition-colors",
        "has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:ring-1 has-[[data-state=checked]]:ring-primary",
        disabled
          ? "cursor-not-allowed opacity-50"
          : "cursor-pointer hover-surface-primary",
        className
      )}
    >
      <RadioGroupItem id={id} disabled={disabled} className="mt-0.5" {...props} />
      <div className="grid gap-1 leading-none">
        <span className="text-sm font-medium">{label}</span>
        {description ? (
          <span className="text-sm text-muted-foreground">{description}</span>
        ) : null}
      </div>
    </label>
  );
}

export { RadioGroup, RadioGroupItem, RadioGroupCard };
